import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { IMG } from "@/lib/images";

const FOUNDER_IMG = IMG.FOUNDER_SMILE_V2;

export const About = () => {
  return (
    <section id="founder" data-testid="founder-section" className="relative py-28 md:py-40 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 relative order-2 lg:order-1"
        >
          <div className="relative aspect-[3/4] overflow-hidden rounded-sm border border-white/5">
            <img
              src={FOUNDER_IMG}
              alt="John, founder of ROADRIG — smiling on his Road Glide 3"
              style={{ objectPosition: "50% 35%" }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="serial text-[10px] text-[#F47A20]">JOHN · FOUNDER · ROADRIG AUSTRALIA</div>
              <div className="serial text-[10px] text-white/50 mt-1">2024 ROAD GLIDE 3 · NSW</div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 hidden md:block glass px-5 py-4 rounded-sm">
            <div className="serial text-[10px] text-white/50">ESTABLISHED</div>
            <div className="h-display text-2xl text-white">2026</div>
          </div>

          {/* Bee — real photo, no AI. Rides with John. */}
          <div
            data-testid="founder-partner-tile"
            className="absolute -bottom-12 -left-4 hidden md:flex items-center gap-3 pl-2 pr-4 py-2 bg-[#0E0E11]/95 backdrop-blur border border-[#F47A20]/30 rounded-sm shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
          >
            <div className="relative w-12 h-12 overflow-hidden rounded-sm border border-white/10 flex-shrink-0">
              <img
                src={IMG.WIFE_BEE}
                alt="Bee — John's wife, riding partner"
                style={{ objectPosition: "60% 30%" }}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="serial text-[9px] tracking-[0.25em] text-[#F47A20] font-bold">RIDES WITH ME</div>
              <div className="text-white text-sm font-bold leading-tight">Bee &middot; my wife</div>
            </div>
          </div>

          {/* Lola — the workshop supervisor. Real photo. */}
          <div
            data-testid="founder-dog-tile"
            className="absolute -bottom-12 -right-4 hidden md:flex items-center gap-3 pl-2 pr-4 py-2 bg-[#0E0E11]/95 backdrop-blur border border-[#F47A20]/30 rounded-sm shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
          >
            <div className="relative w-12 h-12 overflow-hidden rounded-sm border border-white/10 flex-shrink-0 bg-black">
              <img
                src={IMG.LOLA}
                alt="Lola — the ROADRIG workshop supervisor"
                style={{ objectPosition: "50% 30%" }}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="serial text-[9px] tracking-[0.25em] text-[#F47A20] font-bold">WORKSHOP SUPERVISOR</div>
              <div className="text-white text-sm font-bold leading-tight">Lola &middot; the Staffy</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7 order-1 lg:order-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="section-mark" />
            <span className="overline">Meet The Founder</span>
          </div>
          <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl" data-testid="founder-heading">
            Hi, I&apos;m <span className="text-[#F47A20]">John.</span>
          </h2>

          <div className="mt-10 space-y-6 text-white/75 text-base sm:text-lg leading-relaxed max-w-2xl">
            <p className="text-white font-semibold">
              I got sick of waiting for someone else to build the rider bag I wanted. So I built it myself.
            </p>
            <p>
              Every ride taught me something.<br />
              Every sketch fixed another problem.<br />
              Every prototype got me one step closer.
            </p>
            <p className="text-white font-semibold">
              ROADRIG wasn&apos;t born in a boardroom. It started in a garage.
            </p>
          </div>

          <a
            href="mailto:hello@roadrig.com.au"
            data-testid="founder-contact"
            className="mt-10 inline-flex items-center gap-3 text-white hover:text-[#F47A20] transition-colors group"
          >
            <span className="w-10 h-10 grid place-items-center border border-white/15 group-hover:border-[#F47A20] rounded-sm transition-colors">
              <Mail size={16} />
            </span>
            <span>
              <span className="serial text-[10px] block text-white/40">DIRECT FROM THE FOUNDER</span>
              <span className="text-base font-semibold">hello@roadrig.com.au</span>
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
